import {Subject} from "rxjs";
import {
  ISoundSliceRequestContext,
  SoundsliceRequestEventTypes,
  SoundsliceTrackVisibilityTypes
} from "./SoundsliceRequestEventTypes";
import {logger} from "../../log/logger";
import {SoundSliceVideoPositionEnum} from "../../soundSlice/soundSliceVideoPositionEnum";

// Soundslice JavaScript API
// @link https://www.soundslice.com/help/en/embedding/javascript-api/38/introduction/

export const sendMessageToSoundsliceIframeSubject = new Subject<SoundsliceRequestEventTypes>();

/**
 * Iframe should subscribe to this using useEffect.
 * Returns unsubscribe function
 * @param callback - function to be executed whenever an event is emitted via call to Subject next()
 */
export function subscribeToSendMessageToSoundsliceIframeEvents(callback: (message: SoundsliceRequestEventTypes) => void){
  logger.vendorSoundSlice.log({message: `subscribeToSendMessageToSoundsliceIframeEvents called`});
  const protectedCallback = (message: SoundsliceRequestEventTypes) => {
    try{
      callback(message);
    }catch(e){
      logger.vendorSoundSlice.error({message: `error encountered in observer of soundslice iframe messages: `, error: e as Error});
    }
  };
  const subscription = sendMessageToSoundsliceIframeSubject.subscribe(protectedCallback);
  return subscription.unsubscribe.bind(subscription);
}

function emitSoundSliceIframe_event(message: SoundsliceRequestEventTypes){
  sendMessageToSoundsliceIframeSubject.next(message);
}

export function soundSliceSdk_pause(state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "pause", state});
}

  export function soundSliceSdk_getNotesAsync(recordingIndex: number, state?: ISoundSliceRequestContext){
    emitSoundSliceIframe_event({type: "getNotes", recordingIndex, state});
}

export function soundSliceSdk_play(state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "play", state});
}

export function soundSliceSdk_stop(state?: ISoundSliceRequestContext){
  soundSliceSdk_pause(state);
}

export function soundSliceSdk_seek(offset: number, state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "seek", offset, state});
}

export function soundSliceSdk_setVideoPosition(position: SoundSliceVideoPositionEnum, state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "setVideoPosition", position, state});
}

export function soundSliceSdk_setSpeed(speed: number, state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "setSpeed", speed, state});
}

export function soundSliceSdk_setVolume(volume: number, state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "setVolume", volume, state});
}

export function soundSliceSdk_getBarCountAsync(state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "getBarCount", state});
}

export function soundSliceSdk_changeAudioByIndex(recordingIndex: number, state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "changeAudioByIndex", recordingIndex, state});
}

export function soundSliceSdk_loadAudioByIndex(recordingIndex: number, state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "loadAudioByIndex", recordingIndex, state});
}

export function soundSliceSdk_setTrackVisibility(visibilityType: SoundsliceTrackVisibilityTypes, isVisible: boolean, state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "setTrackVisibility", visibilityType, isVisible, state});
}

export function emitNote_changed(barIndex: number, trackIndex: number, voiceIndex: number, beatIndex: number, noteIndex: number, color: string = "#00f", state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "editNote", barIndex, trackIndex, voiceIndex, beatIndex, noteIndex, color, state});
}

/**
 * Zoom notation
 * @param zoom - value should be between -25 and 25
 * @param state
 */
export function soundSliceSdk_setZoom(zoom: number, state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "setZoom", zoom, state});
}

export function soundSliceSdk_getCurrentTimeAsync(state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "getCurrentTime", state});
}

export function soundSliceSdk_setLoop(startTimeSeconds: number, endTimeSeconds: number, state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "setLoop", startTimeSeconds, endTimeSeconds, state});
}

export function soundSliceSdk_clearLoop(state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "clearLoop", state});
}

export function soundSliceSdk_getDurationAsync(state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "getDuration", state});
}

export function soundSliceSdk_editNote(barIndex: number, trackIndex: number, voiceIndex: number, beatIndex: number, noteIndex: number, color: string = "#00f", state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "editNote", barIndex, trackIndex, voiceIndex, beatIndex, noteIndex, color, state});
}

export function soundSliceSdk_drawTempNote(barIndex: number, trackIndex: number, midi: number, barOffsetPercent: number, color: string, state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "drawTempNote", barIndex, trackIndex, midi, barOffsetPercent, color, state});
}

export function soundSliceSdk_clearTempNotes(state?: ISoundSliceRequestContext){
  emitSoundSliceIframe_event({type: "clearTempNotes", state});
}

export function soundSliceSdk_setPlayheadType(playheadType: number, state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "setPlayheadType", playheadType, state});
}

export function soundSliceSdk_setHighlightCurrentBar(highlightCurrentBar: boolean, state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "setHighlightCurrentBar", highlightCurrentBar, state});
}

export function soundSliceSdk_setProportionalNotation(isNotationProportional: boolean, state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "setProportionalNotation", isNotationProportional, state});
}

export function soundSliceSdk_setProportionalSize(proportionalSize: number, state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "setProportionalSize", proportionalSize, state});
}
export function soundSliceSdk_setNotationLayout(layout: number, state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "setLayout", layout, state});
}
export function soundSliceSdk_setPagesPerScreen(pageCount: number, state?: ISoundSliceRequestContext) {
  emitSoundSliceIframe_event({type: "setPagesPerScreen", pageCount, state});
}